import {
    useState,
    useEffect,
    useCallback,
    useRef,
    MouseEventHandler,
} from 'react';
import { TablePaginationProps } from '@mui/material/TablePagination';
import { SelectChangeEvent, SelectProps } from '@mui/material/Select';
import {
    DataGridProps,
    GridSortDirection,
    GridSortModel,
} from '@mui/x-data-grid';
import { useStore } from '@ui/hooks/useStore';
import useDictionary from '@ui/hooks/useDictionary';
import getSortComparator from '@utils/getSortComparator';
import { IState } from './IState';
import { IRow } from '@store/Reports.store';

const useReports = () => {
    const { devices, groups, user, lang, download, telemetry, reports } =
        useStore();
    const getWord = useDictionary();

    const initState: IState = {
        page: 0,
        rows: [],
        columns: [],
        visibilitySettingsState: false,
        visibilitySettingsEl: null,
    };
    const [state, setState] = useState(initState);

    const visibilitySettingsRef = useRef<HTMLDivElement | null>(null);
    const refs = {
        visibilitySettingsRef,
    };

    const handlePageChange: TablePaginationProps['onPageChange'] = useCallback(
        (e, page) => {
            setState((prev) => ({ ...prev, page }));
        },
        [],
    );
    const handleDownloadClick = useCallback(async () => {
        const cvm = reports.columnVisibilityModel;
        const { rows, columns } = state;
        let field: string | undefined;
        let sort: GridSortDirection;
        if (reports.sortModel.length > 0) {
            field = reports.sortModel[0].field;
            sort = reports.sortModel[0].sort;
        }
        const header = columns
            .filter((col) => cvm[col.field])
            .map((col) => col.headerName);
        const cols = columns
            .filter((col) => cvm[col.field])
            .map((col) => col.field);
        let dataRows: IRow[];
        if (field) {
            const _field: string = field;
            const sortComparator = getSortComparator(_field);
            if (sort === 'asc')
                dataRows = [...rows].sort((a, b) => {
                    const v1 = a[_field];
                    const v2 = b[_field];
                    return sortComparator(v1, v2);
                });
            else if (sort === 'desc')
                dataRows = [...rows].sort((a, b) => {
                    const v1 = a[_field];
                    const v2 = b[_field];
                    return sortComparator(v1, v2) * -1;
                });
            else dataRows = rows;
        } else dataRows = rows;
        const data = dataRows.map((row) => cols.map((col) => row[col]));
        const filename = getWord(reports.report);
        await download.downloadCustomData(header, data, filename);
    }, [state.rows, state.columns, reports.sortModel]);
    const handleSortModelChange: DataGridProps['onSortModelChange'] =
        useCallback((sortModel: GridSortModel) => {
            reports.setSortModel(sortModel);
        }, []);
    const handleUnsort = useCallback(() => {
        reports.resetSortModel();
    }, []);
    const handleAscSort = useCallback((field: string) => {
        const sortModel: GridSortModel = [{ field, sort: 'asc' }];
        reports.setSortModel(sortModel);
    }, []);
    const handleDescSort = useCallback((field: string) => {
        const sortModel: GridSortModel = [{ field, sort: 'desc' }];
        reports.setSortModel(sortModel);
    }, []);
    const handleHideCol = useCallback((field: string) => {
        reports.hideCol(field);
    }, []);
    const handleToggleCol: MouseEventHandler<HTMLButtonElement> = useCallback(
        (e) => {
            const { id: field } = e.target as HTMLButtonElement;
            if (field) reports.toggleCol(field);
        },
        [],
    );
    const handleShowAllCol = useCallback(() => {
        reports.showAllCol();
    }, []);
    const handleHideAllCol = useCallback(() => {
        reports.hideAllCol();
    }, []);
    const handleVisibilitySettingsStateChange = useCallback(() => {
        setState((prev) => ({
            ...prev,
            visibilitySettingsState: !prev.visibilitySettingsState,
            visibilitySettingsEl: visibilitySettingsRef.current,
        }));
    }, []);
    const handleVisibilitySettingsStateClose = useCallback(() => {
        setState((prev) => ({
            ...prev,
            visibilitySettingsState: false,
        }));
    }, []);
    const handeLimitChange: SelectProps<number>['onChange'] = useCallback(
        (e: SelectChangeEvent<number>) => {
            const limit = +e.target.value;
            setState((prev) => ({ ...prev, page: 0 }));
            telemetry.setLimit(limit);
        },
        [],
    );
    const handlers = {
        handlePageChange,
        handleDownloadClick,
        handleSortModelChange,
        handleUnsort,
        handleAscSort,
        handleDescSort,
        handleVisibilitySettingsStateChange,
        handleVisibilitySettingsStateClose,
        handleHideCol,
        handleToggleCol,
        handleShowAllCol,
        handleHideAllCol,
        handeLimitChange,
    };

    useEffect(() => {
        return () => {
            telemetry.resetSelectedDevice();
        };
    }, []);
    useEffect(() => {
        const columns = [...reports.columns];
        setState((prev) => ({ ...prev, columns }));
    }, [reports.columns]);
    useEffect(() => {
        const rows = [...reports.rows];
        setState((prev) => ({ ...prev, rows }));
    }, [reports.rows]);

    return {
        devices,
        groups,
        lang,
        user,
        telemetry,
        reports,
        state,
        handlers,
        refs,
    };
};

export default useReports;
